const SEO_ACRONYMS: Record<string, string> = {
  abpa: 'ABPA',
  afp: 'AFP',
  alp: 'ALP',
  alt: 'ALT',
  amh: 'AMH',
  ana: 'ANA',
  anca: 'ANCA',
  ast: 'AST',
  b12: 'B12',
  cbc: 'CBC',
  cea: 'CEA',
  covid: 'COVID',
  'covid-19': 'COVID-19',
  crp: 'CRP',
  ct: 'CT',
  dheas: 'DHEAS',
  dna: 'DNA',
  ecg: 'ECG',
  esr: 'ESR',
  fsh: 'FSH',
  g6pd: 'G6PD',
  hba1c: 'HbA1c',
  hbsag: 'HBsAg',
  hcg: 'hCG',
  hdl: 'HDL',
  hiv: 'HIV',
  ict: 'ICT',
  igg: 'IgG',
  igm: 'IgM',
  kft: 'KFT',
  ldl: 'LDL',
  lft: 'LFT',
  lh: 'LH',
  mri: 'MRI',
  ohpg: 'OHPG',
  pcr: 'PCR',
  psa: 'PSA',
  rbc: 'RBC',
  'rt-pcr': 'RT-PCR',
  sgot: 'SGOT',
  sgpt: 'SGPT',
  t3: 'T3',
  t4: 'T4',
  tibc: 'TIBC',
  tsh: 'TSH',
  vdrl: 'VDRL',
  vldl: 'VLDL',
  wbc: 'WBC',
}

const LOWERCASE_WORDS = new Set([
  'a',
  'an',
  'and',
  'as',
  'at',
  'by',
  'for',
  'from',
  'in',
  'of',
  'on',
  'or',
  'the',
  'to',
  'with',
  'without',
])

function formatSeoToken(token: string, index: number) {
  const match = token.match(/^([^a-zA-Z0-9]*)(.*?)([^a-zA-Z0-9]*)$/)
  if (!match) return token

  const [, prefix, value, suffix] = match
  const normalizedValue = value.toLowerCase()
  const acronym = SEO_ACRONYMS[normalizedValue]
  if (acronym) return `${prefix}${acronym}${suffix}`
  if (index > 0 && LOWERCASE_WORDS.has(normalizedValue))
    return `${prefix}${normalizedValue}${suffix}`

  const formattedValue = normalizedValue
    .split('-')
    .map(
      (part) =>
        SEO_ACRONYMS[part] || part.charAt(0).toUpperCase() + part.slice(1)
    )
    .join('-')
  return `${prefix}${formattedValue}${suffix}`
}

export function formatSeoDisplayName(name: string) {
  return name
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map(formatSeoToken)
    .join(' ')
}

export function getBaseTestName(name: string) {
  return formatSeoDisplayName(name).replace(/\s+test$/i, '')
}

export function getCityItemDisplayName(name: string, city: string) {
  const displayName = formatSeoDisplayName(name)
  const formattedCity = formatSeoDisplayName(city)
  const escapedCity = formattedCity.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  return displayName
    .replace(new RegExp(`(?:\\s*[-–—]\\s*|\\s+in\\s+)${escapedCity}$`, 'i'), '')
    .trim()
}

export const getCityTestDisplayName = getCityItemDisplayName

export function getTestAbbreviation(name: string) {
  return (
    getBaseTestName(name)
      .match(/\(([^)]+)\)/)?.[1]
      ?.trim() || ''
  )
}

export function getGenericTestMetadata(name: string) {
  const testName = getBaseTestName(name)
  const abbreviation = getTestAbbreviation(testName)
  const shortName = abbreviation || testName

  return {
    title: `${testName} Test at Home: Test Price & Preparation`,
    description: `Book ${shortName} tests online at Apollo Diagnostics. Check the ${shortName} test price, purpose, normal range, preparation, report time, and home sample collection. Get accurate reports from trusted NABL-accredited labs.`,
    h1: `${testName} Test`,
  }
}

export function getCityTestMetadata(name: string, city: string) {
  const testName = getCityItemDisplayName(name, city).replace(/\s+test$/i, '')
  const abbreviation = getTestAbbreviation(testName)
  const shortName = abbreviation || testName

  return {
    title: `${testName} in ${city}: Price, Book Home Collection`,
    description: `Book ${testName} test online at Apollo Diagnostics. Know ${shortName} test price, schedule home sample pickup and get quick, accurate reports. Call us on 04069467075.`,
    h1: `${testName} in ${city}`,
  }
}

export function getPackageMetadata(name: string, city: string) {
  const packageName = getCityItemDisplayName(name, city)
  const h1Name = /health checkup$/i.test(packageName)
    ? packageName
    : `${packageName} Health Checkup`

  return {
    title: `Book ${packageName} Test in ${city}: Home Sample Collection`,
    description: `Book ${packageName} Health Checkup online from Apollo Diagnostics. Get Upto 25% Off, home sample collection, certified labs, online reports & a hassle-free process at the best price.`,
    h1: `${h1Name} in ${city}`,
  }
}
